/**
 * Реестр доменов и подразделов — единственное место, где перечислено
 * содержимое сайдбара и protected-роутов. Меняем здесь — меняется и в
 * меню AppShell, и в AppRouter.
 *
 * `countries` — лист показывается только в перечисленных странах.
 * `permission` placeholder для шага 6 (permission registry + can()).
 */

import type {ReactNode} from 'react';
import {createElement} from 'react';
import {
  AppstoreOutlined,
  BankOutlined,
  HomeOutlined,
  NotificationOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
} from '@ant-design/icons';

import {type CountryCode, isNestApiEnabled} from '@services/utils';

export type DomainId = 'home' | 'sales' | 'catalog' | 'marketing' | 'finance' | 'team' | 'system';

export type DomainLeaf = {
  id: string;
  path: string;
  title: string;
  permission: string | null;
  countries?: readonly CountryCode[];
  /**
   * `false` — пункт выключен в этой сборке. Для интеграций, которые
   * включаются env-ключом: страна тут ни при чём, панель может
   * появиться где угодно.
   */
  enabled?: boolean;
};

export type DomainSection = {
  id: DomainId;
  path: string;
  title: string;
  icon: ReactNode;
  permission: string | null;
  children: readonly DomainLeaf[];
};

export const DOMAINS: readonly DomainSection[] = [
  {
    id: 'home',
    path: '/',
    title: 'Главная',
    icon: createElement(HomeOutlined),
    permission: 'home.view',
    children: [],
  },

  {
    id: 'sales',
    path: '/sales',
    title: 'Продажи',
    icon: createElement(ShoppingCartOutlined),
    permission: 'sales.orders.view',
    children: [
      {id: 'orders', path: '/sales/orders', title: 'Заказы', permission: 'sales.orders.view'},
      /* Брони живут отдельным разделом: их выкупают, снимают, отдают
         распространителю — и в списке заказов «в продаже» (SALES_BOOKED)
         вообще не показывались. Ключ menu.booked существовал с самого
         начала и был роздан ролям, но никем не проверялся. */
      {id: 'booked', path: '/sales/booked', title: 'Брони', permission: 'menu.booked'},
      {
        id: 'customers',
        path: '/sales/customers',
        title: 'Клиенты',
        permission: 'sales.customers.view',
      },
      {
        id: 'tickets',
        path: '/sales/tickets',
        title: 'Билеты',
        permission: 'tickets.view',
      },
      {
        id: 'refunds',
        path: '/sales/refunds',
        title: 'Возвраты',
        permission: 'refunds.view',
      },
      {
        id: 'discounts',
        path: '/sales/discounts',
        title: 'Скидки',
        permission: 'sales.discounts.view',
      },
      {
        id: 'transplant',
        path: '/sales/transplant',
        title: 'Пересадка',
        permission: 'sales.transplant.view',
      },
    ],
  },

  {
    id: 'catalog',
    path: '/catalog',
    title: 'Каталог',
    icon: createElement(AppstoreOutlined),
    permission: 'catalog.events.view',
    children: [
      {
        id: 'events',
        path: '/catalog/events',
        title: 'События',
        permission: 'catalog.events.view',
      },
      {
        id: 'theaters',
        path: '/catalog/theaters',
        title: 'Площадки',
        permission: 'catalog.theaters.view',
      },
      {
        id: 'regions',
        path: '/catalog/regions',
        title: 'Регионы',
        permission: 'catalog.regions.view',
      },
    ],
  },

  {
    id: 'marketing',
    path: '/marketing',
    title: 'Маркетинг',
    icon: createElement(NotificationOutlined),
    permission: 'marketing.banners.view',
    children: [
      {
        id: 'banners',
        path: '/marketing/banners',
        title: 'Баннеры',
        permission: 'marketing.banners.view',
      },
      {
        id: 'content',
        path: '/marketing/content',
        title: 'Контент-страницы',
        permission: 'marketing.content.view',
      },
      {
        id: 'mailing',
        path: '/marketing/mailing',
        title: 'Рассылки',
        permission: 'marketing.mailing.send',
        countries: ['kg'],
      },
    ],
  },

  {
    id: 'finance',
    path: '/finance',
    title: 'Финансы',
    icon: createElement(BankOutlined),
    permission: 'finance.reports.view',
    children: [
      {
        id: 'reports',
        path: '/finance/reports',
        title: 'Отчёты',
        permission: 'finance.reports.view',
      },
      {
        id: 'annual-reports',
        path: '/finance/annual-reports',
        title: 'Годовые отчёты',
        permission: 'finance.annual_reports.view',
      },
      {id: 'tax', path: '/finance/tax', title: 'Налоги', permission: 'finance.tax.view'},
      {
        id: 'payment-methods',
        path: '/finance/payment-methods',
        title: 'Способы оплаты',
        permission: 'finance.payment_methods.view',
      },
    ],
  },

  {
    id: 'team',
    path: '/team',
    title: 'Команда',
    icon: createElement(TeamOutlined),
    permission: 'team.users.view',
    children: [
      {id: 'users', path: '/team/users', title: 'Пользователи', permission: 'team.users.view'},
      {
        id: 'organizations',
        path: '/team/organizations',
        title: 'Организации',
        permission: 'team.organizations.view',
      },
      {
        id: 'my-partners',
        path: '/team/my-partners',
        title: 'Каналы продаж',
        permission: 'team.partners_my.view',
      },
    ],
  },

  {
    id: 'system',
    path: '/system',
    title: 'Система',
    icon: createElement(SettingOutlined),
    permission: 'system.sms_codes.view',
    children: [
      {
        id: 'sms-codes',
        path: '/system/sms-codes',
        title: 'SMS-коды',
        permission: 'system.sms_codes.view',
      },
      {
        id: 'sms-platforms',
        path: '/system/sms-platforms',
        title: 'SMS-платформы',
        permission: 'system.sms_platforms.view',
      },
      {
        id: 'api-services',
        path: '/system/api-services',
        title: 'API-сервисы',
        permission: 'system.api_services.view',
      },
      {
        id: 'app-stats',
        path: '/system/app-stats',
        title: 'Статистика app',
        permission: 'system.app_stats.view',
        countries: ['kg'],
      },
      {
        id: 'session-settings',
        path: '/system/session-settings',
        title: 'Настройки сеанса',
        permission: 'system.session_settings.view',
        // Живёт на Nest-панели: нет `VITE_NEST_API_URL` — нет пункта.
        enabled: isNestApiEnabled,
      },
    ],
  },
];
